import { LookupType } from "@prisma/client";

import { lookupTypeToText } from "~/modules/lookups/lookup-type";

/**
 * Search words for each Settings menu link, so the Settings search finds a page by the settings inside it, not
 * only by its name (the logo and currency live on Org Details, FOB on Shipping Options, the default GL on Accounts).
 *
 * Every Settings link must carry its words; the link type makes the field required. Rules for adding or changing
 * a word:
 * - it names something the user can see or change on that page (including inside the components the page
 *   renders), or is the page's own title where it differs from the menu label;
 * - it is a visible label, title or placeholder on the page, the standard short form or plain name of such a
 *   label (GL, FOB, BOL, UOM, CSR, SDS, HS code), or the words customers actually use when asking for it;
 * - leave out generic interface words, invented synonyms, two-letter short forms that match inside other words
 *   (AR, AP, SO), and fields that only refer to something another page owns;
 * - a setting that an org-level feature flag or org setting can hide inside its page gets no words, so a search
 *   never sends an org to a setting it does not have; a setting behind a user permission keeps its words, since
 *   the page is still where it lives;
 * - write plural forms people say ("users", "custom fields"); the singular still matches inside the plural;
 * - do not repeat a word already in the link's label or its group's label.
 *
 * Opt-in order documents keep their words beside their labels in `orderDocumentMetadataBySlug`. As there, `as const`
 * keeps every list readonly for importers and `satisfies` still fails the build on a word that is not a string.
 */
export const SETTINGS_SEARCH_WORDS = {
  // Admin
  orgDetails: [
    "logo",
    "logomark",
    "large logo",
    "branding",
    "white label",
    "currency",
    "multi-currency",
    "timezone",
    "time zone",
    "legal name",
    "company name",
    "business address",
    "phone number",
    "contact information",
  ],
  remittanceAddress: ["remit to"],
  userManagement: ["users", "invite", "pending invites", "roles", "org role", "permissions", "sales person"],
  integrations: [
    "QuickBooks",
    "QuickBooks Online",
    "QBO",
    "Salesforce",
    "CRM",
    "Fathom",
    "Read AI",
    "meeting recorders",
    "accounting sync",
  ],
  noteRecovery: ["auto-created notes", "AI links", "strip links", "meeting notes", "email notes", "ingestion run"],

  // Documents
  borderCrossing: ["default border crossing notes", "international settings"],
  invoice: [
    "invoice text",
    "proforma",
    "pro forma",
    "footer",
    "compact footer",
    "claims notice",
    "remittance instructions",
    "wire",
    "ACH",
    "customer statement",
    "lot numbers",
    "invoice number",
    "filename",
    "direct ship",
    "blind ship",
    "hide product family",
    "price column",
    "package price",
    "per UOM price",
    "unit of measure",
    "UOM",
    "kilograms",
  ],
  purchaseOrder: ["PO text", "PO", "terms of service", "terms and conditions", "T&C"],
  emergencyInfo: ["InfoTrac", "info track", "CHEMTREC", "shipping emergency", "emergency response"],
  billOfLading: [
    "BOL",
    "legal disclaimers",
    "terms and conditions",
    "T&C",
    "straight",
    "consignor",
    "consignee",
    "carrier description",
    "carrier contact",
    "commodity",
    "ERG",
    "emergency response guide",
    "producer",
    "pieces",
    "emergency flags",
    "CHEMTREC",
    "ERAP",
    "certification band",
    "shipper certification",
    "DOT",
    "demerge",
    "demurrage",
    "carrier liability",
    "additional terms",
    "header",
    "footer",
    "merge tokens",
    "attribute wiring",
    "country of mfg",
    "country of origin",
    "unit of measure",
    "UOM",
  ],
  soConfirmation: [
    "sales order confirmation",
    "order confirmation",
    "order acknowledgment",
    "confirmation note",
    "customer pickup",
    "pickup instructions",
    "producer",
    "quantity units",
    "MSDS",
    "SDS",
    "safety data sheet",
    "safety disclaimer",
    "drum hazard",
    "empty drum",
    "footer",
    "unit of measure",
    "UOM",
  ],
  packingList: [
    "packing slip",
    "DOT",
    "hazardous materials",
    "hazmat",
    "placards",
    "shipper's checklist",
    "shipper certification",
    "header",
    "footer",
  ],
  warehouseRelease: ["header", "footer", "shipping instructions"],
  pickUpRequest: ["pickup", "pickup request", "lot numbers", "unit of measure", "UOM"],
  documentOptions: ["optional documents", "opt-in", "turn on", "border", "customs", "Canada", "carrier instructions"],

  // System
  certificationTypes: ["cert", "certificate", "expiry", "expiration", "expiry notice", "documentation required"],
  automations: [
    "regulatory",
    "compliance",
    "certificate trigger",
    "attribute trigger",
    "block orders",
    "flag orders",
    "rules",
    "expired certificate",
  ],
  emailTemplates: [
    "cover email",
    "subject",
    "cc",
    "bcc",
    "template variables",
    "invoice",
    "sales order",
    "purchase order",
    "AR statement",
    "price letter",
    "credit note",
    "supplier payment",
  ],
  // Words already in "Sent Emails" or the Email group are left out.
  sentEmails: [
    "delivery status",
    "bounced",
    "delayed",
    "marked as spam",
    "no report yet",
    "not tracked",
    "sender",
    "recipient",
    "subject",
    "date range",
  ],
  customOutputs: ["labels", "label template", "document template", "drum label", "barcode", "hazard symbols", "GHS", "4x6"],
  identifiers: [
    "numbering",
    "number sequence",
    "prefix",
    "order number",
    "invoice number",
    "PO number",
    "payment number",
    "starting number",
  ],
  warehouses: ["3PL", "warehouse address", "warehouse contact"],
  salesPeople: ["salesperson", "sales rep", "linked user"],
  customerServiceRepresentatives: ["CSR", "default CSR", "assigned to"],
  supplierRepresentatives: ["supplier rep", "vendor rep"],
  // The lookup type labels come from code so these words follow the page's own dropdown types.
  shippingOptions: [...Object.values(LookupType).map(lookupTypeToText), "lookups", "dropdown", "drop down", "ship via"],
  tags: ["category", "tag color", "restricted entities"],
  lotStates: ["lot status", "quarantine", "quarantined", "hold"],
  inventoryManagement: ["initial inventory", "initial cost", "lot cost", "inventory adjustment", "manual lots", "COGS"],

  // Products
  productFamilyAttributes: ["value options", "dropdown", "drop down", "custom fields", "catalog attributes"],
  productCodeAttributes: ["value options", "dropdown", "drop down", "custom fields", "SKU"],
  packageTypes: [
    "packaging type",
    "container",
    "drum",
    "tote",
    "IBC",
    "pail",
    "unit of measure",
    "UOM",
    "tare weight",
    "gross weight",
    "capacity",
    "variable bulk",
    "pricing lock",
    "per unit pricing",
    "UN spec code",
    "dimensions",
  ],
  pallets: ["shipping platform", "skid", "crate", "stackable", "returnable", "reusable", "max load"],

  // Sales Orders
  checkListItems: ["checklist"],
  blockers: ["credit limit", "credit hold"],
  salesOrderItemLabelTemplates: ["line item labels", "print labels", "barcode", "label dimensions"],
  marginSettings: [
    "delivered margin",
    "gross margin",
    "fulfillment costs",
    "delivered cost",
    "landed cost",
    "cost classes",
    "recovery handling",
    "customer charges",
    "unclassified",
  ],

  // Order Item Templates
  salesOrderItemTemplates: [
    "freight charge",
    "non-product line items",
    "fees",
    "surcharge",
    "sales tax",
    "revenue account",
    "cost account",
    "unit price",
    "exclude from commission",
    "exclude from margin",
  ],
  taxSettings: ["jurisdiction", "ship-to tax", "tax type", "tax rate", "county", "state tax", "assignment automation"],
  purchaseOrderItemTemplates: [
    "PO",
    "freight charge",
    "inbound freight",
    "landed cost",
    "duty",
    "tariff",
    "cost allocation",
    "split costs",
    "default cost",
    "cost account",
    "exclude from margin",
  ],
  workOrderItemTemplates: ["work order cost templates", "cost templates", "blending fee", "default cost", "cost account"],

  // Accounting
  accounts: [
    "chart of accounts",
    "general ledger",
    "GL",
    "ledger accounts",
    "default accounts",
    "default GL",
    "account types",
    "asset",
    "liability",
    "equity",
    "revenue",
    "expense",
    "account groups",
    "income statement",
    "P&L",
    "profit and loss",
    "cost of goods sold",
    "COGS",
    "accounts receivable",
    "accounts payable",
    "retained earnings",
    "inventory in transit",
    "purchases clearing",
    "taxes payable",
    "sales tax",
    "credit card",
    "discounts taken",
    "FX gain",
    "FX loss",
    "starting balances",
  ],
  bankAccounts: [
    "ACH",
    "payment file",
    "routing number",
    "account number",
    "TIN",
    "tax ID",
    "EIN",
    "deposits",
    "undeposited funds",
    "credit card",
  ],
  paymentTerms: [
    "terms of payment",
    "net days",
    "net 30",
    "discount days",
    "early payment discount",
    "cash against documents",
    "COD",
    "credit terms",
  ],
  transactionTemplates: [
    "journal entry",
    "JE",
    "recurring journal entry",
    "recurring entry",
    "accrual entry",
    "payroll entry",
    "template transaction",
    "debit",
    "credit",
    "calculated lines",
  ],

  // Billing and Account
  subscriptionAndBilling: [
    "payment method",
    "credit card",
    "account balance",
    "credit balance",
    "contract",
    "next billing date",
    "billing activity",
    "ShelfCycle invoice",
  ],
  manage: ["my account", "profile", "password", "notifications"],

  // Super Admin
  superOrgManagement: [
    "go live",
    "onboarding",
    "setup and launch",
    "disable org",
    "Clerk",
    "danger zone",
    "accounting cutover",
    "cutover date",
    "delete sales orders",
    "delete purchase orders",
  ],
  superRoles: ["permissions", "custom roles", "default roles", "RBAC"],
  superOrgSettings: ["feature flags", "setting keys"],
  superApiKeys: ["public API", "scopes", "revoke", "acting user", "token"],
  superPlaidSetup: ["bank feed", "sandbox", "credentials", "Live Receivables", "feed maintenance"],
  superImporter: ["import", "CSV", "bulk import", "upload", "validate", "import failures"],
  superWeightFixer: ["zero weight", "weight discrepancies", "net weight", "tare weight", "gross weight"],
  superLotFixer: [
    "lot health",
    "phantom inventory",
    "negative inventory",
    "missing inventory",
    "zero cost",
    "allocation mismatch",
    "orphaned receipt",
    "qty on hand",
  ],
  superCostFlowChecker: ["cost mismatches", "lot consumption", "order item cost", "GL correction", "downstream cascade", "COGS"],
  superInventoryManagement: [
    "merge products",
    "edit lots",
    "product catalog",
    "CAS number",
    "freight class",
    "UN number",
    "packing group",
    "NMFC",
    "move lot",
  ],
  superOrderKpiFixer: ["subtotals", "cost discrepancy", "sales discrepancy", "margin", "profit", "recalculate"],
  superAiEmbeddings: ["vector", "AI search", "embed all", "orphaned embeddings", "dry run"],
  superParkedInboundEmail: ["notes@", "forwarding", "forwarded email", "unattributed", "quarantine", "spam", "resolve"],
  superSubledgerDiagnostics: [
    "GL",
    "reconciliation",
    "accounts receivable",
    "accounts payable",
    "open receivables",
    "open payables",
    "balance sheet",
    "out of balance",
    "unbalanced transactions",
    "inventory snapshot",
    "drift",
    "credit notes",
    "COGS",
    "inventory in transit",
  ],
  /**
   * No "base", "output", "template" or "super": the rules above exclude words already in this
   * link's own label or its group's. What is left is what an operator would actually type —
   * the thing they are looking for is usually the bill of lading, or the fact that ShelfCycle
   * ships it, rather than the machinery.
   */
  superBaseOutputTemplates: ["bill of lading", "BOL", "platform org", "ShelfCycle base", "publish", "republish", "fork"],
} as const satisfies Record<string, readonly string[]>;
